/**
 * Condition diagram for a handover or return.
 *
 * Uses the same top-down vehicle and zone names as the damage assessment
 * (see DamageHotspotSelector) so a mark made on a courtesy car speaks the same
 * language as the rest of the app — a scuff logged here can be described the
 * same way as damage on a quote.
 */

import React from 'react';
import { X } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

const SEDAN_IMAGE = `${process.env.PUBLIC_URL || ''}/sedan-top.png`;

// Mirrors the geometry in DamageHotspotSelector so the two read identically.
const ZONES = [
  { id: 'front', label: 'Front', area: { top: '15%', left: '35%', width: '30%', height: '20%' } },
  { id: 'front-right', label: 'Front right', area: { top: '20%', left: '60%', width: '25%', height: '25%' } },
  { id: 'right', label: 'Right side', area: { top: '40%', left: '65%', width: '25%', height: '20%' } },
  { id: 'rear-right', label: 'Rear right', area: { top: '60%', left: '60%', width: '25%', height: '25%' } },
  { id: 'rear', label: 'Rear', area: { top: '65%', left: '35%', width: '30%', height: '20%' } },
  { id: 'rear-left', label: 'Rear left', area: { top: '60%', left: '15%', width: '25%', height: '25%' } },
  { id: 'left', label: 'Left side', area: { top: '40%', left: '10%', width: '25%', height: '20%' } },
  { id: 'front-left', label: 'Front left', area: { top: '20%', left: '15%', width: '25%', height: '25%' } },
  { id: 'roof', label: 'Roof', area: { top: '40%', left: '40%', width: '20%', height: '20%' } }
];

const EXTRA_ZONES = [
  { id: 'interior', label: 'Interior' },
  { id: 'wheels', label: 'Wheels / tyres' },
  { id: 'glass', label: 'Glass' },
  { id: 'underside', label: 'Underside' }
];

const SEVERITIES = [
  { value: 'light', label: 'Light', className: 'bg-yellow-400 border-yellow-500' },
  { value: 'moderate', label: 'Moderate', className: 'bg-orange-500 border-orange-600' },
  { value: 'heavy', label: 'Heavy', className: 'bg-red-500 border-red-600' }
];

const severityClass = (value) => SEVERITIES.find((s) => s.value === value)?.className || SEVERITIES[0].className;

const ConditionDiagram = ({ zones = [], onChange, readOnly = false }) => {
  const marked = (id) => zones.find((z) => z.zone === id);

  const toggleZone = (id, label) => {
    if (readOnly) return;
    if (marked(id)) {
      onChange(zones.filter((z) => z.zone !== id));
    } else {
      onChange([...zones, { zone: id, label, severity: 'light', note: '' }]);
    }
  };

  const updateZone = (id, patch) => {
    if (readOnly) return;
    onChange(zones.map((z) => (z.zone === id ? { ...z, ...patch } : z)));
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <div className="group/diagram relative mx-auto aspect-square w-full max-w-64 select-none">
          <img src={SEDAN_IMAGE} alt="Vehicle from above" className="h-full w-full object-contain opacity-90" />

          {ZONES.map((zone) => {
            const mark = marked(zone.id);
            return (
              <button
                key={zone.id}
                type="button"
                disabled={readOnly}
                onClick={() => toggleZone(zone.id, zone.label)}
                title={zone.label}
                aria-label={`${zone.label}${mark ? ` — ${mark.severity} damage` : ''}`}
                aria-pressed={Boolean(mark)}
                className={cn(
                  'absolute rounded-md border-2 transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  mark
                    ? cn(severityClass(mark.severity), 'opacity-80')
                    : 'border-transparent opacity-0 hover:border-sky-400 hover:bg-sky-400/20 hover:opacity-100 group-hover/diagram:opacity-40',
                  readOnly && 'cursor-default'
                )}
                style={zone.area}
              />
            );
          })}
        </div>

        <div className="mt-2 flex flex-wrap justify-center gap-1.5">
          {EXTRA_ZONES.map((zone) => {
            const mark = marked(zone.id);
            return (
              <Button
                key={zone.id}
                type="button"
                size="sm"
                variant={mark ? 'secondary' : 'outline'}
                disabled={readOnly}
                onClick={() => toggleZone(zone.id, zone.label)}
                className="h-7 text-xs"
              >
                {zone.label}
              </Button>
            );
          })}
        </div>

        {!readOnly && (
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            Tap a panel to record existing damage
          </p>
        )}
      </div>

      <div className="space-y-2">
        {zones.length === 0 ? (
          <div className="flex h-full min-h-32 items-center justify-center rounded-md border border-dashed text-center text-xs text-muted-foreground">
            No damage marked — the vehicle is recorded as clean
          </div>
        ) : (
          zones.map((mark) => (
            <div key={mark.zone} className="rounded-md border p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{mark.label || mark.zone}</span>
                {!readOnly && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => toggleZone(mark.zone, mark.label)}
                    aria-label={`Remove ${mark.label || mark.zone}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="mt-1.5 flex gap-1">
                {SEVERITIES.map((severity) => (
                  <button
                    key={severity.value}
                    type="button"
                    disabled={readOnly}
                    onClick={() => updateZone(mark.zone, { severity: severity.value })}
                    className={cn(
                      'flex-1 rounded border px-1.5 py-0.5 text-[11px] transition-colors',
                      mark.severity === severity.value
                        ? cn(severity.className, 'font-medium text-white')
                        : 'border-border text-muted-foreground hover:bg-accent'
                    )}
                  >
                    {severity.label}
                  </button>
                ))}
              </div>

              <Input
                value={mark.note || ''}
                disabled={readOnly}
                onChange={(e) => updateZone(mark.zone, { note: e.target.value })}
                placeholder="Note (optional)"
                className="mt-1.5 h-7 text-xs"
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConditionDiagram;
